import React, { useEffect, useState } from "react";
import { FileDown, FileText, Loader2, Plus, Trash2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import {
  createStream,
  deleteStream,
  listPrograms,
  listStreams,
  streamReportCsvUrl,
  streamReportPdfUrl,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { STREAM } from "@/constants/testIds";
import BudgetProgress from "@/components/BudgetProgress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";

export default function Streams() {
  const { user } = useAuth();
  const { toast } = useToast();
  const canManage = user?.role === "organization_admin" || user?.role === "admin" || user?.role === "treasurer";

  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const [name, setName] = useState("");
  const [kind, setKind] = useState("grant");
  const [funder, setFunder] = useState("");
  const [total, setTotal] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [desc, setDesc] = useState("");
  const [progIds, setProgIds] = useState([]);
  const [working, setWorking] = useState(false);

  const load = async () => {
    const [s, p] = await Promise.all([listStreams(), listPrograms()]);
    setItems(s);
    setPrograms(p);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      await createStream({
        name,
        kind,
        funder: funder || null,
        total_amount: parseFloat(total),
        period_start: start || null,
        period_end: end || null,
        description: desc || null,
        program_ids: progIds,
      });
      setName(""); setFunder(""); setTotal(""); setStart(""); setEnd(""); setDesc(""); setProgIds([]); setShowForm(false);
      toast({ title: kind === "grant" ? "Grant created" : "Fund stream created" });
      await load();
    } catch (err) {
      toast({ title: "Could not create", description: err?.response?.data?.detail, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this stream? Linked expenses will keep their reference but lose the budget rollup.")) return;
    try {
      await deleteStream(id);
      toast({ title: "Deleted" });
      await load();
    } catch (err) {
      toast({ title: "Could not delete", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const grants = items.filter((s) => s.kind === "grant");
  const funds = items.filter((s) => s.kind === "fund_stream");

  return (
    <>
      <PageHeader title="Grants & Fund Streams" subtitle="Restricted funding sources mapped to programs.">
        {canManage && (
          <button
            data-testid={STREAM.newButton}
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
          >
            <Plus size={14} /> {showForm ? "Cancel" : "New stream"}
          </button>
        )}
      </PageHeader>
      <PageBody>
        {showForm && (
          <form onSubmit={submit} className="border border-border bg-card rounded-md p-6 mb-8 grid sm:grid-cols-12 gap-4">
            <label className="sm:col-span-5">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Name</span>
              <input data-testid={STREAM.nameInput} required value={name} onChange={(e) => setName(e.target.value)} className="td-input mt-1.5" placeholder="Kellogg Hunger Grant 2026" />
            </label>
            <label className="sm:col-span-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Type</span>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid={STREAM.kindSelect} className="td-input h-auto py-2.5 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="grant">Grant</SelectItem>
                  <SelectItem value="fund_stream">Fund stream</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <label className="sm:col-span-4">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Funder</span>
              <input data-testid={STREAM.funderInput} value={funder} onChange={(e) => setFunder(e.target.value)} className="td-input mt-1.5" placeholder="Kellogg Foundation" />
            </label>
            <label className="sm:col-span-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total amount</span>
              <input data-testid={STREAM.totalInput} required type="number" step="0.01" min="0" value={total} onChange={(e) => setTotal(e.target.value)} className="td-input mono mt-1.5" placeholder="0.00" />
            </label>
            <label className="sm:col-span-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Period start</span>
              <input data-testid={STREAM.startInput} type="date" value={start} onChange={(e) => setStart(e.target.value)} className="td-input mt-1.5" />
            </label>
            <label className="sm:col-span-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Period end</span>
              <input data-testid={STREAM.endInput} type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="td-input mt-1.5" />
            </label>
            <label className="sm:col-span-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Programs</span>
              <select multiple value={progIds} onChange={(e) => setProgIds(Array.from(e.target.selectedOptions).map((o) => o.value))} className="td-input mt-1.5 h-auto min-h-[44px]" size={Math.min(4, Math.max(2, programs.length))}>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="sm:col-span-12">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Description</span>
              <textarea data-testid={STREAM.descInput} rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} className="td-input mt-1.5" placeholder="What is this restricted to?" />
            </label>
            <div className="sm:col-span-12 flex justify-end">
              <button data-testid={STREAM.submit} disabled={working} className="rounded-md bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60 inline-flex items-center gap-1.5">
                {working ? <Loader2 size={14} className="animate-spin" /> : null} Create {kind === "grant" ? "grant" : "fund stream"}
              </button>
            </div>
          </form>
        )}

        <Section title="Grants" subtitle="External restricted funding." items={grants} onDelete={remove} canManage={canManage} />
        <Section title="Fund streams" subtitle="Internal designated giving (e.g., capital, scholarships)." items={funds} onDelete={remove} canManage={canManage} />
        {items.length === 0 && (
          <div className="border border-dashed border-border rounded-md p-12 text-center">
            <div className="serif text-xl text-primary">No streams yet</div>
            <p className="text-sm text-muted-foreground mt-1">Add a grant or fund stream to start tracking restricted dollars.</p>
          </div>
        )}
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}

function Section({ title, subtitle, items, onDelete, canManage }) {
  if (items.length === 0) return null;
  return (
    <div className="mb-10">
      <div className="mb-4">
        <div className="serif text-xl text-primary">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((s) => (
          <div key={s.id} data-testid={STREAM.row(s.id)} className="border border-border bg-card rounded-md p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="serif text-xl text-primary truncate">{s.name}</div>
                {s.funder && <div className="text-xs text-muted-foreground mt-0.5 truncate">{s.funder}</div>}
                {(s.period_start || s.period_end) && (
                  <div className="text-[11px] text-muted-foreground mono mt-1">
                    {s.period_start || "—"} → {s.period_end || "—"}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</div>
                <div className="mono text-base text-primary tabular">${Number(s.total_amount).toLocaleString()}</div>
              </div>
            </div>
            <div className="mt-5">
              <BudgetProgress value={Number(s.spent_amount)} max={Number(s.total_amount)} />
            </div>
            <div className="mt-5 flex items-center gap-2">
              <a
                data-testid={STREAM.downloadPdf(s.id)}
                href={streamReportPdfUrl(s.id) + `?_t=${Date.now()}`}
                onClick={(e) => {
                  e.preventDefault();
                  const tok = localStorage.getItem("td_session");
                  fetch(streamReportPdfUrl(s.id), { headers: { Authorization: `Bearer ${tok}` } })
                    .then((r) => r.blob())
                    .then((b) => { const u = URL.createObjectURL(b); window.open(u); });
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background hover:bg-accent px-3 py-1.5 text-xs"
              >
                <FileText size={13} /> PDF report
              </a>
              <a
                data-testid={STREAM.downloadCsv(s.id)}
                href={streamReportCsvUrl(s.id) + `?_t=${Date.now()}`}
                onClick={(e) => {
                  e.preventDefault();
                  const tok = localStorage.getItem("td_session");
                  fetch(streamReportCsvUrl(s.id), { headers: { Authorization: `Bearer ${tok}` } })
                    .then((r) => r.blob())
                    .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `${s.name.replace(/\s+/g, "_")}.csv`; a.click(); });
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background hover:bg-accent px-3 py-1.5 text-xs"
              >
                <FileDown size={13} /> CSV
              </a>
              {canManage && (
                <button
                  onClick={() => onDelete(s.id)}
                  className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                >
                  <Trash2 size={13} /> Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
